import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TextField, Button, CardMedia, IconButton, Input } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { useFormik } from "formik";
import * as Yup from "yup";

import useAuth from "../../hooks/useAuth";
import { UploadMedia } from "../../utils/mediaUpload";
import MainContainer from "../../layout/MainContainer";
import { showToast, showError } from "../../constants/toasts";
import { usePutUpdateProfileMutation } from "../../services/auth";

const EditProfile = () => {
  const user = useAuth();
  const navigate = useNavigate();
  const [updateProfile] = usePutUpdateProfileMutation();

  const [profileImage, setProfileImage] = useState(user?.image || "");

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullName: user?.name || "",
      email: user?.email || "",
      phone: user?.phoneNumber || "",
      countryCode: user?.countryCode?.replace("+", "") || "",
    },
    validationSchema: Yup.object({
      fullName: Yup.string()
        .min(2, "Full name must be at least 2 characters")
        .max(50, "Full name too long")
        .required("Full name is required"),
      email: Yup.string()
        .email("Invalid email format")
        .required("Email is required"),
      phone: Yup.string()
        .matches(/^[0-9]{7,15}$/, "Enter a valid phone number")
        .required("Phone number is required"),
      countryCode: Yup.string().required("Country code is required"),
    }),
    onSubmit: async (values) => {
      const body = {
        name: values.fullName,
        phoneNumber: values.phone,
        countryCode: `+${values.countryCode}`,
        image: profileImage || "ww.",
      };

      try {
        const response = await updateProfile(body).unwrap();
        if (response?.statusCode === 200) {
          showToast("Profile updated successfully");
          navigate("/dashboard");
        }
      } catch (error) {
        console.log(error);
        showError("Failed to update profile");
      }
    },
  });

  const handleProfileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data) {
      setProfileImage(imageUrl.data);
    }
  };

  return (
    <MainContainer>
      <div className="main_title">
        <h1>Edit Profile</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
          <span onClick={() => navigate("/manage-profile")}>
            Manage Profile
          </span>{" "}
          - Edit Profile
        </p>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_100">
              <label>Upload Image</label>
              {profileImage ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia
                        component="img"
                        image={profileImage}
                        alt="photo"
                      />
                    </figure>
                    <CloseIcon onClick={() => setProfileImage("")} />
                  </div>
                </div>
              ) : (
                <label className="upload_image" htmlFor="icon-button-file">
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{
                      accept: "image/png,image/jpeg",
                    }}
                    onChange={(e: any) => {
                      handleProfileUpload(e);
                      e.target.value = "";
                    }}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img
                        src="/static/images/user_placeholder.png"
                        alt="icon"
                      />
                      <span className="upload_img_icon">
                        <img src="/static/images/edit2_icon.svg" alt="icon" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>

            <div className="control_group w_33">
              <label>Full Name</label>
              <TextField
                name="fullName"
                placeholder="Full Name"
                fullWidth
                value={formik.values.fullName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.fullName && Boolean(formik.errors.fullName)
                }
                helperText={formik.touched.fullName && formik.errors.fullName}
              />
            </div>

            <div className="control_group w_33">
              <label>Email</label>
              <TextField
                name="email"
                placeholder="Email"
                fullWidth
                disabled
                value={formik.values.email}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </div>

            <div className="control_group w_33">
              <label>Phone Number</label>
              <PhoneInput
                enableSearch
                value={formik.values.countryCode + formik.values.phone}
                country={"ae"}
                inputClass="phoneInput"
                onChange={(value, country: any) => {
                  const dialCode = country?.dialCode;
                  const phoneNumber = value.replace(dialCode, "");
                  formik.setFieldValue("phone", phoneNumber);
                  formik.setFieldValue("countryCode", dialCode);
                }}
                inputStyle={{ width: "100%" }}
                buttonClass="phoneBtn"
                placeholder="Phone Number"
              />
              {(formik.touched.phone && formik.errors.phone) ||
              formik.errors.countryCode ? (
                <div className="error_text">
                  {formik.errors.phone || formik.errors.countryCode}
                </div>
              ) : null}
            </div>
          </div>

          <div className="form_btn">
            <Button className="btn btn_primary" type="submit">
              Submit
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default EditProfile;
